import { useCallback, useContext, useMemo, useState } from "react";
import { Center, Loader, SimpleGrid, Stack, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";

import { useSetActionButtons } from "../services/ActionButtonsContext";
import { AuthContext } from "../services/context";
import { getCurrentWorkspace } from "../services/userConfig";
import { useGetStations, useCreateStation } from "../api/checkInStation";
import StationCard from "../components/CheckIn/StationCard";
import StationSettingsModal from "../components/CheckIn/StationSettingsModal";

export default function CheckIn() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const eventId = getCurrentWorkspace(user)?.eventId;

  const { data: stations, isPending } = useGetStations(eventId);
  const { mutate: createStation, isPending: isCreating } = useCreateStation();
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const handleCreate = useCallback(
    (settings) => {
      if (!eventId) return;
      createStation(
        { eventId, ...settings },
        {
          onSuccess: ({ code }) => navigate(`/app/check-in/print/${code}`),
        }
      );
    },
    [createStation, eventId, navigate]
  );

  const handleOpen = useCallback(
    (station) => navigate(`/app/check-in/print/${station.code}`),
    [navigate]
  );

  const actions = useMemo(
    () => [
      {
        label: "New station",
        icon: <IconPlus />,
        onClick: () => setIsCreateOpen(true),
      },
    ],
    []
  );

  useSetActionButtons(actions);

  if (isPending) {
    return (
      <Center py="xl">
        <Loader />
      </Center>
    );
  }

  if (!stations?.length) {
    return (
      <>
        <Stack align="center" gap="xs" py="xl">
          <Text fw={600}>No active stations</Text>
          <Text c="dimmed" size="sm">
            Create a station to get a pairing code for the scanning phone.
          </Text>
        </Stack>
        {isCreateOpen && (
          <StationSettingsModal
            isLoading={isCreating}
            onConfirm={handleCreate}
            onClose={() => setIsCreateOpen(false)}
          />
        )}
      </>
    );
  }

  return (
    <>
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
        {stations.map((station) => (
          <StationCard key={station.id} station={station} onOpen={handleOpen} />
        ))}
      </SimpleGrid>
      {isCreateOpen && (
        <StationSettingsModal
          isLoading={isCreating}
          onConfirm={handleCreate}
          onClose={() => setIsCreateOpen(false)}
        />
      )}
    </>
  );
}
